import type {
  BrowserNotificationRuntime,
  NotificationPermissionState,
} from './browser-notification-runtime'
import {
  beginWebPushSubscriptionActivation,
  captureWebPushInstallationBinding,
  compensateFailedWebPushRegistration,
  confirmWebPushSubscriptionRegistration,
  createWebPushBindingToken,
  currentWebPushBindingToken,
  currentWebPushInstallationOwner,
  currentWebPushInstallationSession,
  decodeVapidPublicKey,
  getOrCreateWebPushInstallationId,
  serializePushSubscription,
  setWebPushInstallationBinding,
  subscribeToPushWithGuardedTimeout,
  type SerializedPushSubscription,
  type WebPushDeviceDeactivationStatus,
} from './web-push-client'
import {
  isActiveWebPushServerState,
  type LocalPushSubscriptionState,
  type WebPushServerState,
} from './web-push-state'

const SERVICE_WORKER_READY_TIMEOUT_MS = 8_000

export function requestWebPushPermissionFromGesture(
  runtime: BrowserNotificationRuntime,
): Promise<NotificationPermissionState> {
  const current = runtime.notificationPermission()
  return current === 'default'
    ? runtime.requestNotificationPermission()
    : Promise.resolve(current)
}

export interface WebPushRegistrationInput extends SerializedPushSubscription {
  expectedUserId: string
  expectedSessionId: string
  installationId: string
  bindingToken: string
}

export interface WebPushEnableOrchestration<TStatus> {
  expectedUserId: string
  expectedSessionId: string
  runtime: BrowserNotificationRuntime
  fetchPublicKey: () => Promise<string>
  fetchStatus: (installationId: string) => Promise<{
    subscriptionState: WebPushServerState
  }>
  register: (input: WebPushRegistrationInput) => Promise<TStatus>
  deactivate: (input: {
    expectedUserId: string
    expectedSessionId: string
    installationId: string
    bindingToken: string
    reason: 'subscription_missing'
  }) => Promise<WebPushDeviceDeactivationStatus>
  cacheStatus: (installationId: string, status: TStatus) => void
  setInstallationId: (installationId: string) => void
  setLocalSubscription: (state: LocalPushSubscriptionState) => void
  setActionError: (failed: boolean) => void
  setActionPending: (pending: boolean) => void
  notifyBrowserTruthChanged: () => void
  requestPassiveReconciliation: () => void
  setPassiveRefreshEndpoint: (endpoint: string) => void
  clearPassiveDeactivation: () => void
  subscriptionTimeoutMs?: number
}

export async function enableWebPushForCurrentDevice<TStatus>({
  expectedUserId,
  expectedSessionId,
  runtime,
  fetchPublicKey,
  fetchStatus,
  register,
  deactivate,
  cacheStatus,
  setInstallationId,
  setLocalSubscription,
  setActionError,
  setActionPending,
  notifyBrowserTruthChanged,
  requestPassiveReconciliation,
  setPassiveRefreshEndpoint,
  clearPassiveDeactivation,
  subscriptionTimeoutMs = SERVICE_WORKER_READY_TIMEOUT_MS,
}: WebPushEnableOrchestration<TStatus>): Promise<void> {
  setActionPending(true)
  setActionError(false)
  try {
    const granted = await requestWebPushPermissionFromGesture(runtime)
    notifyBrowserTruthChanged()
    if (granted !== 'granted')
      return

    await runtime.withWebPushLifecycleLock(async () => {
      let activationBinding: ReturnType<
        typeof captureWebPushInstallationBinding
      > | null = null

      const activationInstallationId = getOrCreateWebPushInstallationId()
      setInstallationId(activationInstallationId)
      const publicKey = await fetchPublicKey()
      const currentServerState = (await fetchStatus(activationInstallationId))
        .subscriptionState
      const existingBindingToken = currentWebPushBindingToken()
      const canReuse
        = isActiveWebPushServerState(currentServerState)
          && currentWebPushInstallationOwner() === expectedUserId
          && currentWebPushInstallationSession() === expectedSessionId
          && !!existingBindingToken
      if (!canReuse) {
        await runtime.unsubscribeFromPush()
        try {
          await runtime.closeWebPushNotifications()
        }
        catch {}
      }

      const bindingToken
        = existingBindingToken && canReuse
          ? existingBindingToken
          : createWebPushBindingToken()
      if (
        !canReuse
        && !setWebPushInstallationBinding(
          expectedUserId,
          expectedSessionId,
          activationInstallationId,
          bindingToken,
        )
      ) {
        await runtime.unsubscribeFromPush()
        throw new Error('Push installation identity could not be stored')
      }
      activationBinding = captureWebPushInstallationBinding()
      if (
        activationBinding.installationId !== activationInstallationId
        || activationBinding.ownerUserId !== expectedUserId
        || activationBinding.ownerSessionId !== expectedSessionId
        || activationBinding.bindingToken !== bindingToken
      ) {
        throw new Error('Push installation identity changed')
      }

      let subscription = canReuse ? await runtime.getPushSubscription() : null
      if (!subscription) {
        const subscriptionBinding
          = beginWebPushSubscriptionActivation(activationBinding)
        if (!subscriptionBinding)
          throw new Error('Push subscription ownership could not be stored')
        activationBinding = subscriptionBinding
        subscription = await subscribeToPushWithGuardedTimeout(
          runtime,
          decodeVapidPublicKey(publicKey),
          activationBinding,
          subscriptionTimeoutMs,
        )
      }

      let next: TStatus
      try {
        next = await register({
          expectedUserId,
          expectedSessionId,
          installationId: activationInstallationId,
          bindingToken,
          ...serializePushSubscription(subscription),
        })
        const confirmedBinding = await confirmWebPushSubscriptionRegistration(
          activationBinding,
          subscription.endpoint,
        )
        if (!confirmedBinding)
          throw new Error('Push subscription adoption could not be stored')
        activationBinding = confirmedBinding
      }
      catch (error) {
        await compensateFailedWebPushRegistration(
          expectedUserId,
          expectedSessionId,
          activationBinding,
          { deactivate },
          runtime,
        )
        throw error
      }

      cacheStatus(activationInstallationId, next)
      setPassiveRefreshEndpoint(subscription.endpoint)
      clearPassiveDeactivation()
      setLocalSubscription('present')
    })
  }
  catch {
    setLocalSubscription('error')
    setActionError(true)
    requestPassiveReconciliation()
  }
  finally {
    setActionPending(false)
  }
}
